import React, { useState, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './Orders.css';
import { startOfMonth, endOfMonth, startOfYear, endOfYear, isWithinInterval } from 'date-fns';
import { toast } from 'react-hot-toast';

const ORDER_STATUS = {
  PENDING: { value: 'pending', label: 'En attente', color: '#f59e0b' },
  CONFIRMED: { value: 'confirmed', label: 'Confirmée', color: '#3b82f6' },
  SHIPPED: { value: 'shipped', label: 'Expédiée', color: '#8b5cf6' },
  DELIVERED: { value: 'delivered', label: 'Livrée', color: '#10b981' },
  RETURNED: { value: 'returned', label: 'Retournée', color: '#ec4899' },
  CANCELLED: { value: 'cancelled', label: 'Annulée', color: '#ef4444' }
};

const ORDERS_PER_PAGE = 10;

// Ajouter cette fonction utilitaire avant le composant Orders
const getStatusIcon = (status) => {
  switch (status) {
    case 'pending':
      return 'clock';
    case 'confirmed':
      return 'check';
    case 'shipped':
      return 'truck';
    case 'delivered':
      return 'box';
    case 'returned':
      return 'undo';
    case 'cancelled':
      return 'times';
    default:
      return 'circle';
  }
};

export default function Orders() {
  const { orders, products, loading, refreshData } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewOrder, setViewOrder] = useState(null);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customRange, setCustomRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Filtrage
  const filteredOrders = orders?.filter(order => {
    const matchesSearch = (
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer?.phone?.includes(searchTerm)
    );
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  // Tri
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (sortConfig.key === 'createdAt') {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedOrders.length / ORDERS_PER_PAGE);
  const paginatedOrders = sortedOrders.slice(
    (currentPage - 1) * ORDERS_PER_PAGE,
    currentPage * ORDERS_PER_PAGE
  );

  // Mise à jour du statut
  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const order = orders.find(o => o.id === orderId);
      const product = products.find(p => p.id === order.product.id);
      
      if (!product) {
        throw new Error('Produit non trouvé');
      }

      let stockAdjustment = 0;
      const currentStatus = order.status;

      // Calculer l'ajustement du stock
      if (['pending', 'confirmed'].includes(currentStatus) && 
          ['shipped', 'delivered'].includes(newStatus)) {
        // Réduire le stock quand on passe à expédié/livré
        stockAdjustment = -order.product.quantity;
      } 
      else if (['shipped', 'delivered'].includes(currentStatus) && 
               ['pending', 'confirmed', 'cancelled', 'returned'].includes(newStatus)) {
        // Remettre le stock quand on revient à en attente/confirmé ou annulé/retourné
        stockAdjustment = +order.product.quantity;
      }

      // Vérifier si le stock est suffisant
      if (stockAdjustment < 0 && (product.stock + stockAdjustment) < 0) {
        toast.error('Stock insuffisant pour cette opération');
        return;
      }

      // Mettre à jour le stock si nécessaire
      if (stockAdjustment !== 0) {
        await updateDoc(doc(db, 'products', product.id), {
          stock: product.stock + stockAdjustment
        });
      }

      // Mettre à jour le statut de la commande
      await updateDoc(doc(db, 'orders', orderId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });

      // Si on est dans le modal de détails, mettre à jour viewOrder
      if (viewOrder && viewOrder.id === orderId) {
        setViewOrder(prev => ({
          ...prev,
          status: newStatus,
          updatedAt: new Date().toISOString()
        }));
      }

      toast.success('Statut de la commande mis à jour avec succès !', {
        position: "top-center",
        autoClose: 3000
      });

      refreshData();

    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast.error('Une erreur est survenue lors de la mise à jour');
    }
  };

  // Ajouter cette fonction de filtrage par période
  const filterOrdersByPeriod = (orders) => {
    const now = new Date();
    let startDate, endDate;

    switch (periodFilter) {
      case 'month':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'year':
        startDate = startOfYear(now);
        endDate = endOfYear(now);
        break;
      case 'custom':
        startDate = customRange.startDate ? new Date(customRange.startDate) : null;
        endDate = customRange.endDate ? new Date(customRange.endDate) : null;
        break;
      default:
        return orders;
    }

    if (periodFilter === 'custom' && (!startDate || !endDate)) {
      return orders;
    }

    return orders.filter(order => {
      const orderDate = new Date(order.createdAt);
      return isWithinInterval(orderDate, { start: startDate, end: endDate });
    });
  };

  // Appliquer le filtre de période
  const periodFilteredOrders = filterOrdersByPeriod(filteredOrders);

  // Calcul des statistiques
  const calculateStats = (orders) => {
    return orders.reduce((stats, order) => {
      stats.totalOrders++;
      stats.totalRevenue += order.totalPrice || 0;
      
      switch (order.status) {
        case 'pending':
          stats.pendingOrders++;
          break;
        case 'delivered':
          stats.deliveredOrders++;
          break;
      }
      
      return stats;
    }, {
      totalOrders: 0,
      totalRevenue: 0,
      pendingOrders: 0,
      deliveredOrders: 0
    });
  };

  const stats = useMemo(() => {
    if (!orders) return {
      orderStats: {
        pending: 0,
        shipped: 0,
        delivered: 0,
        returned: 0,
        cancelled: 0
      }
    };

    return {
      orderStats: {
        pending: orders.filter(o => o.status === 'pending').length,
        shipped: orders.filter(o => o.status === 'shipped').length,
        delivered: orders.filter(o => o.status === 'delivered').length,
        returned: orders.filter(o => o.status === 'returned').length,
        cancelled: orders.filter(o => o.status === 'cancelled').length
      }
    };
  }, [orders]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Chargement des commandes...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="orders-container">
        <div className="orders-header">
          <h1>Gestion des Commandes</h1>
          <div className="orders-actions">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Rechercher une commande..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(ORDER_STATUS).map(([key, { value, label }]) => (
                <option key={key} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="period-filters">
            <select 
              value={periodFilter} 
              onChange={(e) => setPeriodFilter(e.target.value)}
              className="period-select"
            >
              <option value="all">Toutes les périodes</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
              <option value="custom">Période personnalisée</option>
            </select>

            {periodFilter === 'custom' && (
              <div className="date-range-picker">
                <input
                  type="date"
                  value={customRange.startDate}
                  onChange={(e) => setCustomRange(prev => ({ 
                    ...prev, 
                    startDate: e.target.value 
                  }))}
                  className="date-input"
                />
                <input
                  type="date"
                  value={customRange.endDate}
                  onChange={(e) => setCustomRange(prev => ({ 
                    ...prev, 
                    endDate: e.target.value 
                  }))}
                  min={customRange.startDate}
                  className="date-input"
                />
              </div>
            )}
          </div>
        </div>

        <div className="stats-cards">
          <div className="stat-card pending">
            <i className="fas fa-clock"></i>
            <div className="stat-info">
              <h3>En Attente</h3>
              <p>{stats.orderStats.pending}</p>
              <span className="stat-label">commandes</span>
            </div>
          </div>

          <div className="stat-card confirmed">
            <i className="fas fa-check"></i>
            <div className="stat-info">
              <h3>Confirmées</h3>
              <p>{stats.orderStats.confirmed}</p>
              <span className="stat-label">commandes</span>
            </div>
          </div>

          <div className="stat-card shipped">
            <i className="fas fa-truck"></i>
            <div className="stat-info">
              <h3>Expédiées</h3>
              <p>{stats.orderStats.shipped}</p>
              <span className="stat-label">commandes</span>
            </div>
          </div>

          <div className="stat-card delivered">
            <i className="fas fa-box-check"></i>
            <div className="stat-info">
              <h3>Livrées</h3>
              <p>{stats.orderStats.delivered}</p>
              <span className="stat-label">commandes</span>
            </div>
          </div>

          <div className="stat-card returned">
            <i className="fas fa-undo"></i>
            <div className="stat-info">
              <h3>Retournées</h3>
              <p>{stats.orderStats.returned}</p>
              <span className="stat-label">commandes</span>
            </div>
          </div>

          <div className="stat-card cancelled">
            <i className="fas fa-times-circle"></i>
            <div className="stat-info">
              <h3>Annulées</h3>
              <p>{stats.orderStats.cancelled}</p>
              <span className="stat-label">commandes</span>
            </div>
          </div>
        </div>

        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>ID Commande</th>
                <th>Client</th>
                <th>Produit</th>
                <th>Livraison</th>
                <th>Total</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map(order => (
                  <tr key={order.id}>
                    <td>#{order.id.slice(-6)}</td>
                    <td>
                      <div className="customer-info">
                      <strong>{order.customer.firstName} {order.customer.lastName}</strong>
                      <span>{order.customer.phone}</span>
                      <span>{order.customer.wilaya}, {order.customer.city}</span>
                      </div>
                    </td>
                    <td>
                    <div className="product-info">
                      <strong>{order.product.name}</strong>
                      <span>{order.product.quantity}x {order.product.price} DZD</span>
                      <span>Sous-total: {order.product.subtotal} DZD</span>
                      </div>
                    </td>
                    <td>
                    <div className="shipping-info">
                      <span className="delivery-type">
                        {order.shipping.type === 'home' ? (
                          <><i className="fas fa-home"></i> Livraison à domicile</>
                        ) : (
                          <><i className="fas fa-building"></i> Récupération au bureau</>
                        )}
                        </span>
                      {order.shipping.isFree ? (
                        <span className="free-shipping-badge">
                          <i className="fas fa-gift"></i> Livraison gratuite
                          <span className="free-reason">{order.shipping.freeReason}</span>
                        </span>
                      ) : (
                        <span>Frais: {order.shipping.price} DZD</span>
                      )}
                      </div>
                    </td>
                  <td>
                    <div className="price-info">
                      <strong>{order.totalPrice} DZD</strong>
                      </div>
                    </td>
                    <td>
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                      className={`status-select ${order.status}`}
                    >
                      {Object.values(ORDER_STATUS).map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                    </td>
                    <td>
                    <div className="actions">
                        <button 
                        onClick={() => setViewOrder(order)}
                          className="btn-view" 
                          title="Voir les détails"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            
            <span className="pagination-info">
              Page {currentPage} sur {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        {selectedOrder && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Modifier le statut de la commande #{selectedOrder.id.slice(-6)}</h2>
                <button className="btn-close" onClick={() => setSelectedOrder(null)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-content">
                <div className="status-options">
                  {Object.entries(ORDER_STATUS).map(([key, { value, label, color }]) => (
                    <button
                      key={key}
                      className="status-option"
                      style={{ 
                        backgroundColor: selectedOrder.status === value ? color : 'transparent',
                        color: selectedOrder.status === value ? 'white' : color,
                        borderColor: color
                      }}
                      onClick={() => handleStatusUpdate(selectedOrder.id, value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {viewOrder && (
          <div className="order-details-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Détails de la commande #{viewOrder.id.slice(-6)}</h2>
                <button onClick={() => setViewOrder(null)} className="close-btn">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="modal-body">
                {/* Section Statut de la commande */}
                <div className="details-section status-section">
                  <h3>Statut de la commande</h3>
                  <div className="status-options">
                    {Object.entries(ORDER_STATUS).map(([key, { value, label, color }]) => (
                      <button
                        key={key}
                        className={`status-option ${viewOrder.status === value ? 'active' : ''}`}
                        style={{ 
                          backgroundColor: viewOrder.status === value ? color : 'transparent',
                          color: viewOrder.status === value ? 'white' : color,
                          borderColor: color
                        }}
                        onClick={() => handleStatusUpdate(viewOrder.id, value)}
                      >
                        <i className={`fas fa-${getStatusIcon(value)}`}></i>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="details-section">
                  <h3>Informations client</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Nom complet:</span>
                    <span className="value">
                      {viewOrder.customer.firstName} {viewOrder.customer.lastName}
                    </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Téléphone:</span>
                    <span className="value">{viewOrder.customer.phone}</span>
                    </div>
                    <div className="detail-item">
                    <span className="label">Adresse:</span>
                    <span className="value">
                      {viewOrder.customer.wilaya}, {viewOrder.customer.city}
                    </span>
                  </div>
                  </div>
                </div>

                <div className="details-section">
                  <h3>Détails du produit</h3>
                      <div className="details-grid">
                  <div className="detail-item">
                    <span className="label">Produit:</span>
                    <span className="value">{viewOrder.product.name}</span>
                  </div>
                        <div className="detail-item">
                          <span className="label">Prix unitaire:</span>
                    <span className="value">{viewOrder.product.price} DZD</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Quantité:</span>
                    <span className="value">{viewOrder.product.quantity}</span>
                        </div>
                        <div className="detail-item">
                    <span className="label">Sous-total:</span>
                    <span className="value">{viewOrder.product.subtotal} DZD</span>
                  </div>
                  </div>
                </div>

                <div className="details-section">
                <h3>Informations de livraison</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="label">Type de livraison:</span>
                    <span className="value">
                      {viewOrder.shipping.type === 'home' 
                        ? 'Livraison à domicile' 
                        : 'Récupération au bureau'}
                      </span>
                    </div>
                  <div className="detail-item">
                    <span className="label">Frais de livraison:</span>
                    <span className="value">
                      {viewOrder.shipping.isFree ? (
                        <span className="free-shipping">
                          Gratuit
                          <small className="free-reason">
                            ({viewOrder.shipping.freeReason})
                          </small>
                        </span>
                      ) : (
                        `${viewOrder.shipping.price} DZD`
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h3>Total de la commande</h3>
                <div className="total-amount">
                  {viewOrder.totalPrice} DZD
              </div>
            </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
} 